import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useAccountWalkthroughs } from "@/components/walkthrough/useAccountWalkthroughs";
import {
  getCookieConsent,
  hasDecidedCookieConsent,
  setCookieConsent,
} from "@/lib/cookieConsent";
import { posthog } from "@/lib/posthog";
import { hashString } from "@/lib/hash";
import { getGradeTierProfile, resolveGradeLevel } from "@shared/gradeTier";
import { testerTrialForSignup } from "@shared/testerCampaign";
import {
  TESTER_TASK_EVENT,
  TESTER_TASKS,
  testerMarker,
  type TesterTaskId,
} from "./testerTasks";

import { TesterContext } from "./TesterStateContext";
export { useTester } from "./TesterStateContext";
export function TesterProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { ready, hasSeen, markSeen } = useAccountWalkthroughs();
  const [consentDecided, setConsentDecided] = useState(hasDecidedCookieConsent);
  const [, refreshClock] = useState(0);
  const [, setRevision] = useState(0);
  const trial = user ? testerTrialForSignup(user.id, user.created_at) : null;
  const welcomeDismissed = hasSeen(testerMarker("welcome"));
  const isKids = getGradeTierProfile(resolveGradeLevel(preferences)).isKids;
  useEffect(() => {
    const change = () => {
      setConsentDecided(hasDecidedCookieConsent());
      setRevision((n) => n + 1);
    };
    window.addEventListener("novari:cookie-consent", change);
    window.addEventListener("storage", change);
    return () => {
      window.removeEventListener("novari:cookie-consent", change);
      window.removeEventListener("storage", change);
    };
  }, []);
  useEffect(() => {
    if (!trial?.active) return;
    const timer = setTimeout(
      () => refreshClock((n) => n + 1),
      Math.max(0, Date.parse(trial.endsAt) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [trial?.active, trial?.endsAt]);
  useEffect(() => {
    let cancelled = false;
    if (!ready || !trial?.active || !welcomeDismissed || isKids || !user) {
      posthog.configureTesterReplay();
      return;
    }
    void hashString(user.id)
      .then((distinctId) => {
        if (!cancelled)
          posthog.configureTesterReplay({
            userId: user.id,
            distinctId,
            campaignId: trial.campaignId,
            tier: trial.tier,
            endsAt: trial.endsAt,
            allowed: true,
          });
      })
      .catch(() => posthog.configureTesterReplay());
    return () => {
      cancelled = true;
      posthog.configureTesterReplay();
    };
  }, [
    ready,
    user?.id,
    trial?.active,
    trial?.endsAt,
    trial?.tier,
    trial?.campaignId,
    isKids,
    welcomeDismissed,
  ]);
  useEffect(() => {
    const finish = (event: Event) => {
      const { userId, task } = (event as CustomEvent).detail ?? {};
      if (
        !ready ||
        !trial?.active ||
        userId !== user?.id ||
        !TESTER_TASKS.some((t) => t.id === task)
      )
        return;
      if (hasSeen(testerMarker(task))) return;
      markSeen(testerMarker(task));
      posthog.capture("tester_task_completed", {
        tester_campaign: trial.campaignId,
        tester_tier: trial.tier,
        task_id: task,
      });
      setRevision((n) => n + 1);
    };
    window.addEventListener(TESTER_TASK_EVENT, finish);
    return () => window.removeEventListener(TESTER_TASK_EVENT, finish);
  }, [ready, trial?.active, user?.id, markSeen]);
  const completed = TESTER_TASKS.filter((task) =>
    hasSeen(testerMarker(task.id)),
  ).map((task) => task.id);
  const welcomePending =
    ready && !!trial?.active && !hasSeen(testerMarker("welcome"));
  const complete = (task: TesterTaskId) => {
    if (!ready || !trial?.active || hasSeen(testerMarker(task))) return;
    markSeen(testerMarker(task));
    posthog.capture("tester_task_completed", {
      tester_campaign: trial.campaignId,
      tester_tier: trial.tier,
      task_id: task,
      self_reported: true,
    });
    setRevision((n) => n + 1);
  };
  const finishWelcome = (recording: boolean) => {
    if (!ready || !trial?.active) return;
    if (!isKids)
      setCookieConsent(recording || !!getCookieConsent()?.analytics, recording);
    markSeen(testerMarker("welcome"));
    setRevision((n) => n + 1);
  };
  return (
    <TesterContext.Provider
      value={{
        trial,
        welcomePending,
        consentDecided,
        completed,
        complete,
        finishWelcome,
      }}
    >
      {children}
    </TesterContext.Provider>
  );
}
